import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, StickyNote, Receipt as ReceiptIcon } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { SimilarItemsPanel } from "./SimilarItemsPanel";

const CHIPS = ["Geschäftsessen mit Kunde", "Mitarbeiteressen", "Benzin Geschäftsfahrt", "Material für Umbau", "Büromaterial", "Trinkgeld CHF", "Gutschrift / Rückerstattung", "Privat bezahlt, Firma"];
const chf = (v) => (v == null ? "–" : `CHF ${Number(v).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const d = (s) => (s ? new Date(s).toLocaleDateString("de-CH") : "–");

/** Notiz zum Beleg: Kontext oben, Schnellwahl-Chips, grosses Textfeld, ähnliche Belege, eine Hauptaktion.
 *  onSave/onSaveRecheck erhalten die IDs der gewählten ähnlichen Belege. */
export function InboxNoteDialog({ item, companyId, value, onChange, onClose, onSave, onSaveRecheck, saving }) {
  const ref = useRef(null);
  const ex = item?.extracted || {};
  const [similar, setSimilar] = useState(null);
  const [selected, setSelected] = useState(new Set());
  useEffect(() => { setTimeout(() => ref.current?.focus(), 50); }, [item?.id]);
  useEffect(() => {
    setSimilar(null); setSelected(new Set());
    if (!item?.id || !companyId) return;
    let active = true;
    apiClient.get(`/companies/${companyId}/inbox/${item.id}/similar`)
      .then((r) => { if (active) setSimilar(r.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [item?.id, companyId]);
  const ids = () => [...selected];
  const toggle = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((p) => (p.size === (similar?.items?.length || 0) ? new Set() : new Set(similar.items.map((s) => s.id))));
  const addChip = (c) => { onChange(value?.trim() ? `${value.trim()}, ${c}` : c); ref.current?.focus(); };
  const onKey = (e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && value?.trim()) { e.preventDefault(); onSaveRecheck(ids()); } };
  const n = selected.size;
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden gap-0" data-testid="inbox-note-dialog">
        <div className="bg-slate-900 text-white px-5 py-4">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold"><StickyNote className="h-4 w-4 text-emerald-300" />Wofür ist dieser Beleg?</DialogTitle>
          <DialogDescription className="text-slate-300 text-xs mt-1">Ein Satz genügt – die KI wählt daraus Konto, Projekt oder Trinkgeld.</DialogDescription>
          <div className="mt-3 flex items-center gap-3 rounded-lg bg-white/10 px-3 py-2 text-sm" data-testid="inbox-note-context">
            <ReceiptIcon className="h-4 w-4 text-slate-300 shrink-0" />
            <div className="min-w-0 flex-1 truncate"><span className="font-medium">{ex.supplier_name || item?.file_name || "Beleg"}</span><span className="text-slate-300"> · {d(ex.invoice_date)}</span></div>
            <div className="font-semibold whitespace-nowrap">{chf(ex.total_amount)}</div>
          </div>
          {ex.suggested_account && <div className="mt-1.5 text-[11px] text-slate-300">Aktuell: Konto {ex.suggested_account}</div>}
        </div>
        <div className="px-5 pt-4 pb-2 space-y-3">
          <div className="flex flex-wrap gap-1.5" data-testid="inbox-note-chips">
            {CHIPS.map((c) => (
              <button key={c} type="button" onClick={() => addChip(c)} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700 hover:border-emerald-400 hover:bg-emerald-50 transition-colors" data-testid={`inbox-note-chip-${c.split(" ")[0].toLowerCase()}`}>{c}</button>
            ))}
          </div>
          <textarea ref={ref} value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={onKey} rows={4}
            placeholder="z.B. «Benzin für Geschäftsfahrt nach Bern», «Essen mit Kunde Meier», «Umbau Rapperswil», «bezahlt 240 – Rest Trinkgeld»"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
            data-testid="inbox-note-textarea" />
          <div className="flex items-center justify-between text-[11px] text-slate-400"><span>Ctrl + Enter = speichern & prüfen</span><span>{(value || "").length} Zeichen</span></div>
          <SimilarItemsPanel similar={similar} selected={selected} onToggle={toggle} onToggleAll={toggleAll} />
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-500" data-testid="inbox-note-cancel">Abbrechen</Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onSave(ids())} disabled={saving} data-testid="inbox-note-save">{saving ? "Speichere…" : n ? `Speichern (${n + 1})` : "Nur speichern"}</Button>
            <Button size="sm" onClick={() => onSaveRecheck(ids())} disabled={saving || !value?.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="inbox-note-save-recheck">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}{saving ? "KI prüft…" : n ? `Für alle ${n + 1} übernehmen & prüfen` : "Speichern & KI prüfen"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
